// Cloudflare Turnstile widget — shared across A01 (SignIn), A05 (Forgot
// Password), and optionally A05b (Reset). Wraps @marsidev/react-turnstile
// (Workers-friendly, ~5 KB).
//
// Token semantics (mirror sub-plan 09 mobile <CaptchaModal>):
//   - On verify: onToken(token) fires → parent enables submit.
//   - Tokens are SINGLE-USE (consumed by Cloudflare siteverify). Caller MUST
//     reset() the widget AND clear local state on EVERY response (2xx OR not).
//   - The widget exposes a ref with .reset() so callers can re-issue.

import { forwardRef, useImperativeHandle, useRef } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Box, Typography } from "@mui/material";
import { ENV } from "@/lib/env";

export interface CaptchaWidgetHandle {
  reset: () => void;
}

interface Props {
  onToken: (token: string | null) => void;
  onError?: (err: unknown) => void;
}

export const CaptchaWidget = forwardRef<CaptchaWidgetHandle, Props>(
  function CaptchaWidget({ onToken, onError }, ref) {
    const innerRef = useRef<TurnstileInstance | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          innerRef.current?.reset();
          onToken(null);
        },
      }),
      [onToken],
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Turnstile
          ref={innerRef}
          siteKey={ENV.TURNSTILE_SITE_KEY}
          onSuccess={(token) => onToken(token)}
          onError={(err) => {
            onToken(null);
            onError?.(err);
          }}
          onExpire={() => {
            onToken(null);
          }}
          options={{ theme: "light", size: "normal" }}
        />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: 12 }}
        >
          Verifying you're human...
        </Typography>
      </Box>
    );
  },
);
